import React from "react";
import { CustomCellRendererProps } from "ag-grid-react";
import { Box, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Typography } from "@platform/design-system";

import { BenefitDescriptionOption } from "../CellEditors/BenefitDescriptionMultiOptionsEditor";

import type { PlanEditorRowData } from "../../types/values.types";

// A benefit option that carries a usable numeric value (empty entries hold null)
type ValidBenefitDescriptionOption = BenefitDescriptionOption & {
  value: number;
};

/**
 * Type guard to check if a value is a valid BenefitDescriptionOption
 */
const isValidOption = (
  option: unknown,
): option is ValidBenefitDescriptionOption =>
  option !== null &&
  typeof option === "object" &&
  "type" in option &&
  "value" in option &&
  (option.type === "dollarAmount" || option.type === "salaryMultiplier") &&
  typeof option.value === "number" &&
  option.value > 0;

/**
 * Format a benefit option for display
 */
const formatOption = (option: ValidBenefitDescriptionOption): string => {
  if (option.type === "dollarAmount") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(option.value);
  }

  // salaryMultiplier
  return `${option.value}x salary`;
};

/**
 * Cell renderer for benefit description multi-options fields
 * Displays a formatted list of dollar amounts and salary multipliers
 */
export const BenefitDescriptionMultiOptionsRenderer: React.FC<
  CustomCellRendererProps<PlanEditorRowData, unknown>
> = (props) => {
  const { value } = props;

  // Handle empty or invalid values
  if (!value || !Array.isArray(value) || value.length === 0) {
    return (
      <Box
        sx={{
          color: "text.secondary",
          fontStyle: "italic",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          width: "100%",
          justifyContent: "center",
        }}
      >
        <EditIcon fontSize="small" sx={{ opacity: 0.5 }} />
        <Typography variant="body-medium">Click to configure</Typography>
      </Box>
    );
  }

  // Validate that all entries have the required structure
  const validOptions = value.filter(isValidOption);

  if (validOptions.length === 0) {
    return (
      <Box
        sx={{
          color: "text.secondary",
          fontStyle: "italic",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          width: "100%",
          justifyContent: "center",
        }}
      >
        <EditIcon fontSize="small" sx={{ opacity: 0.5 }} />
        <Typography variant="body-medium">Click to configure</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ cursor: "pointer", py: 0.5 }}>
      <Stack spacing={0.25}>
        {validOptions.map((option: ValidBenefitDescriptionOption) => (
          <Typography
            key={`${option.type}-${option.value}`}
            variant="body-medium"
          >
            {formatOption(option)}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};
